/**
 * Interactive Components Extraction
 *
 * Parse chat message content to find structured interactive
 * components such as todos, checklists and rich diagram tags.
 *
 * Primary responsibilities:
 * - Detect bracket-based interactive tags in message content
 * - Build serializable InteractiveComponent metadata objects
 * - Reuse existing helpers for code blocks and checklists
 *
 * Dependencies:
 * - lib/messages (code block parsing)
 * - lib/utils/checklist-parser (checklist parsing)
 */

import type {
  InteractiveComponent,
  InteractiveComponentType
} from '@/types/interactive-components'
import { parseCodeBlocksAndContent } from './messages'
import { parseChecklistContent } from './utils/checklist-parser'

const COMPONENT_TAG_REGEX =
  /\\?\[(LOSI_APP_PREVIEW|LOSI_PLANNER_DAY|SPACE_ITEM_CARD|VISUAL_ARTIFACT|YOUTUBE_VIDEO|YOUTUBE_VIDEO_LIST|FLASHCARD|TODO|QUIZ|TIMELINE|CODE_DIFF|POLL|PROGRESS|RATING|CHECKLIST|FORM|MIND_MAP|ARGUMENT_MAP|FLOW_CHART|GANTT_CHART|DATA_DASHBOARD|SYSTEM_MAP|BUSINESS_CANVAS|TABS|DEFINITION)\\?\](.*?)\\?\[\/\1\\?\]/gs

// --------------

const safeJsonParse = <T>(value: string): T | null => {
  try {
    return JSON.parse(value) as T
  } catch {
    return null
  }
}

const buildLabelFromText = (text: string, fallback: string): string => {
  const trimmed = text.trim()
  if (!trimmed) return fallback

  const firstLine = trimmed.split('\n')[0]
  const label = firstLine.length > 80 ? `${firstLine.slice(0, 77)}...` : firstLine

  return label || fallback
}

const nextId = (messageId: string, index: number) => `${messageId}:${index}`

// --------------

interface ExtractionContext {
  content: string
  messageId: string
  chatId: string
  createdAt?: string
}

// --------------

const extractBracketComponents = ({
  content,
  messageId,
  chatId,
  createdAt
}: ExtractionContext): InteractiveComponent[] => {
  const components: InteractiveComponent[] = []

  let index = 0

  const pushComponent = (partial: Omit<InteractiveComponent, 'id'>) => {
    components.push({
      ...partial,
      id: nextId(messageId, index)
    })
    index += 1
  }

  for (const match of content.matchAll(COMPONENT_TAG_REGEX)) {
    const fullMatch = match[0]
    const type = match[1] as InteractiveComponentType
    const inner = match[2] ?? ''
    const position = match.index ?? 0

    // Base shape shared by all specialised branches
    const base = {
      chatId,
      messageId,
      position,
      createdAt
    }

    if (type === 'SPACE_ITEM_CARD') {
      const parsed = safeJsonParse<Record<string, unknown>>(inner)
      const title = typeof parsed?.title === 'string' ? parsed.title : ''

      pushComponent({
        ...base,
        type: 'SPACE_ITEM_CARD',
        label: buildLabelFromText(title, 'Space item'),
        data: parsed ?? { raw: inner }
      })
      continue
    }

    if (type === 'LOSI_APP_PREVIEW') {
      const parsed = safeJsonParse<Record<string, unknown>>(inner)
      const title = typeof parsed?.title === 'string' ? parsed.title : ''
      const prompt = typeof parsed?.prompt === 'string' ? parsed.prompt : ''

      pushComponent({
        ...base,
        type: 'LOSI_APP_PREVIEW',
        label: buildLabelFromText(title || prompt, 'Losi App preview'),
        data: parsed ?? { raw: inner }
      })
      continue
    }

    if (type === 'LOSI_PLANNER_DAY') {
      const parsed = safeJsonParse<Record<string, unknown>>(inner)
      const title = typeof parsed?.title === 'string' ? parsed.title : ''
      const date = typeof parsed?.date === 'string' ? parsed.date : ''

      pushComponent({
        ...base,
        type: 'LOSI_PLANNER_DAY',
        label: buildLabelFromText(title || date, 'Day plan'),
        data: parsed ?? { raw: inner }
      })
      continue
    }

    if (type === 'TODO') {
      const items = inner.split('|').map((item) => item.trim()).filter(Boolean)
      const label = items[0] || 'Todo list'

      pushComponent({
        ...base,
        type: 'TODO',
        label,
        data: { items }
      })
      continue
    }

    if (type === 'FLASHCARD') {
      const [questionRaw] = inner.split('|')
      const label = buildLabelFromText(questionRaw || '', 'Flashcard')

      pushComponent({
        ...base,
        type: 'FLASHCARD',
        label,
        data: {
          raw: inner
        }
      })
      continue
    }

    if (type === 'QUIZ') {
      const [questionRaw] = inner.split('|')
      const label = buildLabelFromText(questionRaw || '', 'Quiz question')

      pushComponent({
        ...base,
        type: 'QUIZ',
        label,
        data: {
          raw: inner
        }
      })
      continue
    }

    if (type === 'CHECKLIST') {
      const parsed = parseChecklistContent(inner)
      const label = parsed.title || 'Checklist'

      pushComponent({
        ...base,
        type: 'CHECKLIST',
        label,
        data: parsed
      })
      continue
    }

    if (type === 'DEFINITION') {
      const mainParts = inner.split('[EXAMPLES]')[0].split('|')
      const term = (mainParts[0] || '').trim()
      const label = term || 'Definition'

      pushComponent({
        ...base,
        type: 'DEFINITION',
        label,
        data: {
          raw: inner,
          term
        }
      })
      continue
    }

    if (type === 'YOUTUBE_VIDEO' || type === 'YOUTUBE_VIDEO_LIST') {
      const parsed = safeJsonParse<Record<string, unknown>>(inner)
      const firstVideo =
        Array.isArray(parsed?.videos) && typeof parsed.videos[0] === 'object'
          ? parsed.videos[0] as Record<string, unknown>
          : null
      const title =
        typeof parsed?.title === 'string'
          ? parsed.title
          : typeof firstVideo?.title === 'string'
            ? firstVideo.title
            : undefined

      pushComponent({
        ...base,
        type,
        label: buildLabelFromText(title || '', 'YouTube videos'),
        data: parsed ?? { raw: inner }
      })
      continue
    }

    if (type === 'VISUAL_ARTIFACT') {
      const parsed = safeJsonParse<Record<string, unknown>>(inner)
      const title = typeof parsed?.title === 'string' ? parsed.title : ''
      const artifactType = typeof parsed?.type === 'string' ? parsed.type : ''

      pushComponent({
        ...base,
        type,
        label: buildLabelFromText(title || artifactType, 'Visual artifact'),
        data: parsed ?? { raw: inner }
      })
      continue
    }

    // JSON-backed component types share similar parsing behaviour
    const jsonBackedTypes: InteractiveComponentType[] = [
      'TIMELINE',
      'CODE_DIFF',
      'POLL',
      'RATING',
      'PROGRESS',
      'FORM',
      'MIND_MAP',
      'ARGUMENT_MAP',
      'FLOW_CHART',
      'GANTT_CHART',
      'DATA_DASHBOARD',
      'SYSTEM_MAP',
      'BUSINESS_CANVAS',
      'TABS'
    ]

    if (jsonBackedTypes.includes(type)) {
      const parsed = safeJsonParse<Record<string, unknown>>(inner)
      const title =
        typeof parsed?.title === 'string'
          ? parsed.title
          : typeof parsed?.question === 'string'
            ? parsed.question
            : undefined

      const label = buildLabelFromText(title || '', type.replace('_', ' ').toLowerCase())

      pushComponent({
        ...base,
        type,
        label,
        data: parsed ?? { raw: inner }
      })
      continue
    }

    // Fallback for any remaining types
    pushComponent({
      ...base,
      type,
      label: buildLabelFromText(inner, type.toLowerCase()),
      data: { raw: fullMatch }
    })
  }

  return components
}

const extractCodePreviewComponents = ({
  content,
  messageId,
  chatId,
  createdAt
}: ExtractionContext): InteractiveComponent[] => {
  const { codeBlocks } = parseCodeBlocksAndContent(content, messageId)

  const components: InteractiveComponent[] = []

  codeBlocks.forEach((block) => {
    if (!block.filename) return

    const label = block.filename

    components.push({
      id: nextId(messageId, block.sequenceNo),
      type: 'CODE_PREVIEW',
      chatId,
      messageId,
      position: block.sequenceNo,
      label,
      createdAt,
      data: {
        filename: block.filename,
        language: block.language
      }
    })
  })

  return components
}

// --------------

export const extractInteractiveComponentsFromContent = (
  content: string,
  messageId: string,
  chatId: string,
  createdAt?: string
): InteractiveComponent[] => {
  if (!content.trim()) return []

  const context: ExtractionContext = {
    content,
    messageId,
    chatId,
    createdAt
  }

  const fromTags = extractBracketComponents(context)
  const fromCode = extractCodePreviewComponents(context)

  if (fromTags.length === 0 && fromCode.length === 0) {
    return []
  }

  return [...fromTags, ...fromCode]
}
